import { useMemo, useState } from 'react';
import { isLowConfidence } from '@dma/shared';
import { DistributionBar } from '../DistributionBar.tsx';
import { fmt } from '../format.ts';
import { LowNBadge, Select } from '../ui.tsx';
import type { PerQuestionDistributionRow } from '../types.ts';

// View 5 — pick a dimension, list its questions with a small distribution bar,
// per-question mean/median/sd and a High Divergence tag.
export function QuestionDistributionView({
  data,
  colors,
}: {
  data: PerQuestionDistributionRow[];
  colors: readonly string[];
}) {
  // Unique dimensions in first-seen order (data is already dimension-grouped).
  const dims = useMemo(() => {
    const seen = new Map<string, string>();
    for (const q of data) if (!seen.has(q.dimensionSlug)) seen.set(q.dimensionSlug, q.dimensionName);
    return [...seen].map(([slug, name]) => ({ slug, name }));
  }, [data]);

  const [slug, setSlug] = useState<string>('');
  const activeSlug = dims.find((d) => d.slug === slug)?.slug ?? dims[0]?.slug ?? '';
  const questions = data.filter((q) => q.dimensionSlug === activeSlug);

  if (dims.length === 0) {
    return <p className="text-sm text-ink-soft">No questions available.</p>;
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="max-w-xs">
        <Select
          id="q-dist-select"
          label="Dimension"
          value={activeSlug}
          onChange={setSlug}
          options={dims.map((d) => ({ value: d.slug, label: d.name }))}
        />
      </div>

      <ul className="flex flex-col divide-y divide-black/5">
        {questions.map((q) => (
          <li key={q.externalRef} className="py-4 first:pt-0 last:pb-0">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-sm text-ink">
                <span className="mr-1.5 font-mono text-xs text-ink-soft">
                  {q.isCustom ? 'CQ' : 'Q'}{q.isCustom ? q.externalRef - 10000 : q.externalRef}
                </span>
                {q.prompt}
                {q.isCustom && (
                  <span className="ml-2 inline-flex items-center rounded-full bg-purple-50 px-2 py-0.5 text-[10px] font-medium text-purple-700 ring-1 ring-inset ring-purple-200">
                    Custom
                  </span>
                )}
              </p>
              {q.highDivergence && (
                <span className="inline-flex items-center rounded-full bg-rose-50 px-2.5 py-0.5 text-xs font-medium text-rose-800 ring-1 ring-inset ring-rose-200">
                  High Divergence
                </span>
              )}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-ink-soft tabular-nums">
              <span className="inline-flex items-center gap-1.5">
                n {q.n}
                {isLowConfidence(q.n) && <LowNBadge />}
              </span>
              <span>Mean {fmt(q.mean)}</span>
              <span>Median {fmt(q.median)}</span>
              <span>SD {fmt(q.sd)}</span>
            </div>
            <div className="mt-2">
              <DistributionBar
                distribution={q.distribution}
                colors={colors}
                compact
                emptyMessage={q.n === 0 ? 'No submitted responses yet.' : undefined}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
